import gsap from "gsap";

function setFilterActive(button, isActive) {
  button.setAttribute("aria-pressed", isActive ? "true" : "false");
  button.classList.toggle("bg-cic-green", isActive);
  button.classList.toggle("bg-transparent", !isActive);
}

function filterMembers(members, categoryId) {
  members.forEach((member) => {
    if (categoryId === "all") {
      member.hidden = false;
      return;
    }

    const ids = (member.dataset.categories || "")
      .split(",")
      .filter(Boolean);
    member.hidden = !ids.includes(categoryId);
  });
}

function isFiltersScrollable(scroller) {
  return scroller.scrollWidth > scroller.clientWidth + 1;
}

function scrollFilterToStart(scroller, track, button, { instant = false } = {}) {
  if (!isFiltersScrollable(scroller)) return;

  const left = Math.max(0, button.offsetLeft - track.offsetLeft);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (instant || reduceMotion) {
    gsap.set(scroller, { scrollLeft: left });
    return;
  }

  gsap.to(scroller, {
    scrollLeft: left,
    duration: 0.45,
    ease: "power2.out",
    overwrite: true,
  });
}

function initFilterDrag(scroller) {
  let isPointerDown = false;
  let didDrag = false;
  let startX = 0;
  let startScrollLeft = 0;
  let activePointerId = null;
  const dragThreshold = 8;

  const endDrag = (event) => {
    if (!isPointerDown || event.pointerId !== activePointerId) return;

    isPointerDown = false;
    activePointerId = null;
    scroller.classList.remove("cursor-grabbing");
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    window.removeEventListener("pointercancel", endDrag);

    if (didDrag) {
      // Block only the synthetic click that follows a drag, not real taps.
      const suppressClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();
        scroller.removeEventListener("click", suppressClick, true);
      };
      scroller.addEventListener("click", suppressClick, true);
    }
  };

  const onPointerMove = (event) => {
    if (!isPointerDown || event.pointerId !== activePointerId) return;

    const deltaX = event.clientX - startX;

    if (!didDrag && Math.abs(deltaX) > dragThreshold) {
      didDrag = true;
      scroller.classList.add("cursor-grabbing");
      gsap.killTweensOf(scroller);
    }

    if (didDrag) {
      scroller.scrollLeft = startScrollLeft - deltaX;
      event.preventDefault();
    }
  };

  scroller.addEventListener("pointerdown", (event) => {
    // Touch/pen keep native overflow scrolling; mouse uses click-drag.
    if (event.pointerType !== "mouse") return;
    if (!isFiltersScrollable(scroller)) return;
    if (event.button !== 0) return;

    isPointerDown = true;
    didDrag = false;
    activePointerId = event.pointerId;
    startX = event.clientX;
    startScrollLeft = scroller.scrollLeft;

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
  });
}

function initTeamFilters(root) {
  const scroller = root.querySelector("[data-about-team-filters]");
  const track = scroller?.querySelector("[data-about-team-track]");
  const filters = Array.from(root.querySelectorAll("[data-about-team-filter]"));
  const members = Array.from(root.querySelectorAll("[data-about-team-member]"));
  if (!filters.length || !members.length) return;

  const allFilter = filters.find((filter) => filter.dataset.category === "all");
  filters.forEach((filter) => {
    setFilterActive(filter, filter === allFilter);
  });
  filterMembers(members, "all");

  if (scroller && track) {
    initFilterDrag(scroller);
  }

  filters.forEach((button) => {
    button.addEventListener("click", () => {
      const categoryId = button.dataset.category;
      if (!categoryId) return;

      filters.forEach((filter) => {
        setFilterActive(filter, filter === button);
      });

      filterMembers(members, categoryId);

      if (scroller && track) {
        scrollFilterToStart(scroller, track, button);
      }
    });
  });
}

function initTeamModal(root) {
  const dialog = root.querySelector("[data-about-team-modal]");
  const body = dialog?.querySelector("[data-about-team-modal-body]");
  const panel = dialog?.querySelector("[data-about-team-modal-panel]");
  const closeButton = dialog?.querySelector("[data-about-team-modal-close]");
  const openButtons = Array.from(root.querySelectorAll("[data-about-team-open]"));
  if (!dialog || !body || !panel || !closeButton || !openButtons.length) return;

  const openModal = (member) => {
    const template = member.querySelector("[data-about-team-modal-content]");
    if (!template) return;

    body.replaceChildren(template.content.cloneNode(true));
    dialog.showModal();
  };

  const closeModal = () => {
    if (dialog.open) dialog.close();
  };

  openButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const member = button.closest("[data-about-team-member]");
      if (member) openModal(member);
    });
  });

  closeButton.addEventListener("click", closeModal);

  dialog.addEventListener("click", (event) => {
    if (!panel.contains(event.target)) closeModal();
  });

  dialog.addEventListener("close", () => {
    body.replaceChildren();
  });
}

export function initAboutTeam() {
  const root = document.querySelector("[data-about-team]");
  if (!root) return;

  initTeamFilters(root);
  initTeamModal(root);
}
